import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { API_ENDPOINTS, apiRequest, getRegistrationsByEventUrl, getTunelUserUrl } from '../config/api';

// =====================================================
// CONVERGÊNCIA 360 - Painel LED Animado (React)
// =====================================================

// Configurações padrão (serão sobrescritas pelos valores da API)
const DEFAULT_CONFIG = {
  numParticles: 200,
  tunnelLength: 2000,
  tunnelRadius: 400,
  nameCount: 0,
  highlightDuration: 5000,
  highlightPosition: { x: 0, y: 10, z: 550 },
  highlightOthersOpacity: 0.1,
  vanishingPointZ: -800,
  cameraZ: 600,
  visibleNamesCount: 100,
  swapInterval: 15000,
  swapPercentage: 0.1,
  plexusNodes: 80,
  plexusConnectionDist: 250,
  maxConnectionsPerNode: 3,
  plexusOpacity: 0.8,
  plexusMinOpacity: 0,
  nodeSize: 2,
  nameNodeSize: 3,
  pulseSpeed: 1.5,
  nodeSpeed: 0.25,
  shortName: false,
  highlightPoolingTime: 20
};

/**
 * Extrai apenas o primeiro e último nome de um nome completo
 * @param fullName - Nome completo
 * @returns Primeiro e último nome separados por espaço
 */
const getShortName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 2) {
    return fullName;
  }
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

// Tipo para as configurações
type ConfigType = typeof DEFAULT_CONFIG;

// Estilos inline
const styles = {
  container: {
    position: 'relative' as const,
    width: '100vw',
    height: '100vh',
    margin: 0,
    padding: 0,
    overflow: 'hidden',
    backgroundColor: '#000',
    fontFamily: "'Segoe UI', 'Arial', sans-serif",
  },
  canvas: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  title: {
    position: 'absolute' as const,
    top: '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '2.5rem',
    fontWeight: 300,
    letterSpacing: '15px',
    color: '#00ffff',
    textShadow: '0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 40px #0088ff, 0 0 80px #0088ff',
    zIndex: 100,
    pointerEvents: 'none' as const,
  },
  controls: {
    position: 'absolute' as const,
    bottom: '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '15px',
    zIndex: 100,
  },
  select: {
    padding: '12px 20px',
    fontSize: '1rem',
    background: 'rgba(0, 40, 60, 0.8)',
    border: '1px solid #00ffff',
    color: '#00ffff',
    borderRadius: '5px',
    cursor: 'pointer',
    minWidth: '250px',
    outline: 'none',
  },
  button: {
    padding: '12px 25px',
    fontSize: '1rem',
    background: 'linear-gradient(135deg, rgba(0, 150, 200, 0.3), rgba(0, 80, 120, 0.5))',
    border: '1px solid #00ffff',
    color: '#00ffff',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase' as const,
    letterSpacing: '2px',
  },
  highlightedName: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '4rem',
    fontWeight: 'bold',
    color: '#00ffff',
    textShadow: '0 0 20px #00ffff, 0 0 40px #00ffff, 0 0 60px #0088ff, 0 0 100px #0088ff',
    opacity: 0,
    transition: 'opacity 0.5s ease',
    pointerEvents: 'none' as const,
    zIndex: 50,
    textAlign: 'center' as const,
    letterSpacing: '8px',
  },
  highlightedNameVisible: {
    opacity: 1,
  },
};

const PainelLED: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleNames, setVisibleNames] = useState<string[]>([]);
  const [selectedName, setSelectedName] = useState('');
  const [highlightedText, setHighlightedText] = useState('');
  const [isHighlightVisible, setIsHighlightVisible] = useState(false);
  const [highlightPosition, setHighlightPosition] = useState({ top: '50%', left: '50%' });
  const [configLoaded, setConfigLoaded] = useState(false);
  const [titlePhrase, setTitlePhrase] = useState(' ');
  const [namesList, setNamesList] = useState<string[]>([]);
  
  // Ref para configuração dinâmica (carregada da API)
  const configRef = useRef<ConfigType>({ ...DEFAULT_CONFIG });
  
  // Refs para estado da animação
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const clockRef = useRef<THREE.Clock | null>(null);
  const animationIdRef = useRef<number>(0);
  
  // Estado da aplicação
  const stateRef = useRef({
    nameObjects: [] as THREE.Sprite[],
    tunnelLines: [] as THREE.Line[],
    particles: [] as any[],
    plexusNodes: [] as THREE.Mesh[],
    plexusLines: [] as THREE.Line[],
    allNodes: [] as THREE.Mesh[],
    visibleNames: [] as string[],
    availableNames: [] as string[],
    highlightedName: null as string | null,
    isHighlighting: false,
    isReturning: false,
    returningSprite: null as THREE.Sprite | null,
    returnProgress: 0,
    exitingSprites: [] as any[],
    enteringSprites: [] as any[],
    lastSwapTime: 0,
    isSwapping: false,
    connectionFadeProgress: 1,
    connectionFadeDirection: 0,
    connectionsRefreshed: false,
  });

  // Função para buscar parâmetros da API
  const fetchParameters = useCallback(async () => {
    try {
      console.log('🔧 [PainelLED] Buscando parâmetros da API...');
      const response = await apiRequest(API_ENDPOINTS.GET_PARAMETERS);
      const data = await response.json();

      if (data.success) {
        const params = data.parameters || {};
        console.log('⚙️ [PainelLED] Parâmetros recebidos:', params);

        // Atualizar CONFIG com valores da API (prefixo CONNECTIONS_)
        const config = configRef.current;
        
        if (params.CONNECTIONS_NUM_PARTICLES) {
          config.numParticles = parseInt(params.CONNECTIONS_NUM_PARTICLES, 10);
        }
        if (params.CONNECTIONS_TUNNEL_LENGTH) {
          config.tunnelLength = parseInt(params.CONNECTIONS_TUNNEL_LENGTH, 10);
        }
        if (params.CONNECTIONS_TUNNEL_RADIUS) {
          config.tunnelRadius = parseInt(params.CONNECTIONS_TUNNEL_RADIUS, 10);
        }
        if (params.CONNECTIONS_HIGHLIGHT_DURATION) {
          config.highlightDuration = parseInt(params.CONNECTIONS_HIGHLIGHT_DURATION, 10);
        }
        if (params.CONNECTIONS_HIGHLIGHT_POSITION_X !== undefined) {
          config.highlightPosition.x = parseFloat(params.CONNECTIONS_HIGHLIGHT_POSITION_X);
        }
        if (params.CONNECTIONS_HIGHLIGHT_POSITION_Y !== undefined) {
          config.highlightPosition.y = parseFloat(params.CONNECTIONS_HIGHLIGHT_POSITION_Y);
        }
        if (params.CONNECTIONS_HIGHLIGHT_POSITION_Z !== undefined) {
          config.highlightPosition.z = parseFloat(params.CONNECTIONS_HIGHLIGHT_POSITION_Z);
        }
        if (params.CONNECTIONS_HIGHLIGHT_OTHERS_OPACITY) {
          config.highlightOthersOpacity = parseFloat(params.CONNECTIONS_HIGHLIGHT_OTHERS_OPACITY);
        }
        if (params.CONNECTIONS_VANISHING_POINT_Z) {
          config.vanishingPointZ = parseInt(params.CONNECTIONS_VANISHING_POINT_Z, 10);
        }
        if (params.CONNECTIONS_CAMERA_Z) {
          config.cameraZ = parseInt(params.CONNECTIONS_CAMERA_Z, 10);
        }
        if (params.CONNECTIONS_VISIBLE_NAMES_COUNT) {
          config.visibleNamesCount = parseInt(params.CONNECTIONS_VISIBLE_NAMES_COUNT, 10);
        }
        if (params.CONNECTIONS_SWAP_INTERVAL) {
          config.swapInterval = parseInt(params.CONNECTIONS_SWAP_INTERVAL, 10);
        }
        if (params.CONNECTIONS_SWAP_PERCENTAGE) {
          config.swapPercentage = parseFloat(params.CONNECTIONS_SWAP_PERCENTAGE);
        }
        if (params.CONNECTIONS_PLEXUS_NODES) {
          config.plexusNodes = parseInt(params.CONNECTIONS_PLEXUS_NODES, 10);
        }
        if (params.CONNECTIONS_PLEXUS_CONNECTION_DIST) {
          config.plexusConnectionDist = parseInt(params.CONNECTIONS_PLEXUS_CONNECTION_DIST, 10);
        }
        if (params.CONNECTIONS_MAX_CONNECTIONS_PER_NODE) {
          config.maxConnectionsPerNode = parseInt(params.CONNECTIONS_MAX_CONNECTIONS_PER_NODE, 10);
        }
        if (params.CONNECTIONS_PLEXUS_OPACITY) {
          config.plexusOpacity = parseFloat(params.CONNECTIONS_PLEXUS_OPACITY);
        }
        if (params.CONNECTIONS_PLEXUS_MIN_OPACITY) {
          config.plexusMinOpacity = parseFloat(params.CONNECTIONS_PLEXUS_MIN_OPACITY);
        }
        if (params.CONNECTIONS_NODE_SIZE) {
          config.nodeSize = parseFloat(params.CONNECTIONS_NODE_SIZE);
        }
        if (params.CONNECTIONS_NAME_NODE_SIZE) {
          config.nameNodeSize = parseFloat(params.CONNECTIONS_NAME_NODE_SIZE);
        }
        if (params.CONNECTIONS_PULSE_SPEED) {
          config.pulseSpeed = parseFloat(params.CONNECTIONS_PULSE_SPEED);
        }
        if (params.CONNECTIONS_NODE_SPEED) {
          config.nodeSpeed = parseFloat(params.CONNECTIONS_NODE_SPEED);
        }
        if (params.CONNECTIONS_SHORT_NAME) {
          config.shortName = params.CONNECTIONS_SHORT_NAME.toLowerCase() === 'true';
        }
        if (params.CONNECTIONS_HIGHLIGHT_POOLING_TIME) {
          config.highlightPoolingTime = parseInt(params.CONNECTIONS_HIGHLIGHT_POOLING_TIME, 10);
        }
        
        // Atualizar título/frase
        if (params.CONNECTIONS_PHRASE) {
          setTitlePhrase(params.CONNECTIONS_PHRASE);
        }

        console.log('✅ [PainelLED] CONFIG atualizado:', config);

        // Buscar nomes do evento se CONNECTIONS_EVENT_ID estiver configurado
        if (params.CONNECTIONS_EVENT_ID) {
          try {
            const eventId = parseInt(params.CONNECTIONS_EVENT_ID, 10);
            console.log('🔧 [PainelLED] Buscando nomes do evento:', eventId);
            const namesUrl = getRegistrationsByEventUrl(eventId, true);
            const namesResponse = await apiRequest(namesUrl);
            const namesData = await namesResponse.json();
            
            if (Array.isArray(namesData)) {
              // Aplicar shortName se configurado
              const processedNames = config.shortName 
                ? namesData.map((name: string) => getShortName(name))
                : namesData;
              // Remover duplicatas mantendo a ordem
              const uniqueNames = [...new Set(processedNames)];
              setNamesList(uniqueNames);
              config.nameCount = uniqueNames.length;
              console.log('✅ [PainelLED] Nomes carregados:', uniqueNames.length, config.shortName ? '(modo nome curto)' : '(nome completo)', `(${processedNames.length - uniqueNames.length} duplicatas removidas)`);
            } else {
              console.warn('⚠️ [PainelLED] Resposta de nomes não é um array, usando lista vazia');
              setNamesList([]);
            }
          } catch (namesError) {
            console.error('❌ [PainelLED] Erro ao buscar nomes:', namesError);
            setNamesList([]);
          }
        } else {
          console.warn('⚠️ [PainelLED] CONNECTIONS_EVENT_ID não configurado, usando lista vazia');
          setNamesList([]);
        }
      } else {
        console.warn('⚠️ [PainelLED] API retornou success=false, usando lista vazia');
        setNamesList([]);
      }
    } catch (error) {
      console.error('❌ [PainelLED] Erro ao buscar parâmetros:', error);
      console.log('🔄 [PainelLED] Usando lista vazia');
      setNamesList([]);
    }
    
    setConfigLoaded(true);
  }, []);

  // Carregar parâmetros ao montar o componente
  useEffect(() => {
    fetchParameters();
  }, [fetchParameters]);

  // Funções auxiliares
  const createNameSprite = (name: string, index: number) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    
    // Medir o texto primeiro para calcular o tamanho necessário do canvas
    const fontSize = 32;
    context.font = `bold ${fontSize}px Arial`;
    const textMetrics = context.measureText(name);
    const textWidth = textMetrics.width;
    
    // Definir tamanho do canvas com padding para garantir que o texto caiba
    const padding = 40;
    const minCanvasWidth = 512;
    const canvasWidth = Math.max(minCanvasWidth, Math.ceil(textWidth + padding));
    const canvasHeight = 64;
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    // Limpar canvas (precisa reconfigurar o font após redimensionar)
    context.fillStyle = 'rgba(0, 0, 0, 0)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = '#ffffff';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(name, canvas.width / 2, canvas.height / 2);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      opacity: 0.8,
      depthTest: false
    });
    
    const sprite = new THREE.Sprite(material);
    sprite.renderOrder = 999;
    
    // Calcular escala proporcional ao tamanho do canvas
    const baseScale = 200;
    const scaleX = (canvasWidth / minCanvasWidth) * baseScale;
    const scaleY = 25;
    sprite.scale.set(scaleX, scaleY, 1);
    
    const CONFIG = configRef.current;
    const angle = Math.random() * Math.PI * 2;
    const radius = 50 + Math.random() * 250;
    const z = CONFIG.cameraZ - 200 + Math.random() * 500;
    const clampedZ = Math.max(CONFIG.vanishingPointZ + 300, Math.min(CONFIG.cameraZ + 100, z));
    
    sprite.position.set(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      clampedZ
    );
    
    sprite.userData = {
      name: name,
      index: index,
      originalPosition: sprite.position.clone(),
      targetPosition: sprite.position.clone(),
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 1.5,
        (Math.random() - 0.5) * 1.5,
        (Math.random() - 0.5) * 2
      ),
      phase: Math.random() * Math.PI * 2,
      amplitude: {
        x: 15 + Math.random() * 50,
        y: 15 + Math.random() * 40,
        z: 30 + Math.random() * 80
      },
      speed: 0.3 + Math.random() * 0.5,
      isHighlighted: false,
      originalOpacity: 0.8,
      baseScale: scaleX
    };
    
    return sprite;
  };

  const createTunnel = (scene: THREE.Scene) => {
    const state = stateRef.current;
    const CONFIG = configRef.current;
    const tunnelGroup = new THREE.Group();
    
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
      depthTest: false
    });
    
    const numLines = 24;
    for (let i = 0; i < numLines; i++) {
      const angle = (i / numLines) * Math.PI * 2;
      const outerRadius = CONFIG.tunnelRadius * 1.5;
      
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array([
        Math.cos(angle) * outerRadius, 
        Math.sin(angle) * outerRadius, 
        CONFIG.cameraZ + 200,
        0, 0, CONFIG.vanishingPointZ
      ]);
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      
      const line = new THREE.Line(geometry, lineMaterial.clone());
      tunnelGroup.add(line);
      state.tunnelLines.push(line);
    }
    
    const ringMaterial = new THREE.LineBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
      depthTest: false
    });
    
    const numRings = 15;
    for (let i = 0; i < numRings; i++) {
      const t = i / numRings;
      const z = THREE.MathUtils.lerp(CONFIG.cameraZ, CONFIG.vanishingPointZ, t);
      const radius = THREE.MathUtils.lerp(CONFIG.tunnelRadius * 1.5, 10, t);
      
      const points = new THREE.Path().absarc(0, 0, radius, 0, Math.PI * 2, false).getPoints(64);
      const ring = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(points),
        ringMaterial.clone()
      );
      ring.position.z = z;
      ring.userData.baseZ = z;
      ring.userData.baseRadius = radius;
      tunnelGroup.add(ring);
      state.tunnelLines.push(ring as any);
    }
    
    const gridMaterial = new THREE.LineBasicMaterial({
      color: 0x0088ff,
      transparent: true,
      opacity: 0.15,
      depthWrite: false,
      depthTest: false
    });
    
    const numRandomLines = 50;
    for (let i = 0; i < numRandomLines; i++) {
      let x, y;
      do {
        x = (Math.random() - 0.5) * 1000;
        y = (Math.random() - 0.5) * 800;
      } while (Math.abs(Math.abs(x) - Math.abs(y)) < 50);
      
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array([
        x, y, CONFIG.cameraZ + 100,
        x * 0.02, y * 0.02, CONFIG.vanishingPointZ
      ]);
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      
      const line = new THREE.Line(geometry, gridMaterial.clone());
      tunnelGroup.add(line);
    }
    
    scene.add(tunnelGroup);
  };

  const createParticles = (scene: THREE.Scene) => {
    const state = stateRef.current;
    const CONFIG = configRef.current;
    const particleGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(CONFIG.numParticles * 3);
    const colors = new Float32Array(CONFIG.numParticles * 3);
    
    for (let i = 0; i < CONFIG.numParticles; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 1500;
      positions[i3 + 1] = (Math.random() - 0.5) * 1000;
      positions[i3 + 2] = Math.random() * (CONFIG.cameraZ - CONFIG.vanishingPointZ) + CONFIG.vanishingPointZ;
      
      const colorIntensity = 0.5 + Math.random() * 0.5;
      colors[i3] = 0;
      colors[i3 + 1] = colorIntensity;
      colors[i3 + 2] = colorIntensity;
    }
    
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      size: 2,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });
    
    const particleSystem = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particleSystem);
    
    state.particles.push({
      system: particleSystem,
      geometry: particleGeometry,
      velocities: Array(CONFIG.numParticles).fill(0).map(() => ({
        x: (Math.random() - 0.5) * 0.5,
        y: (Math.random() - 0.5) * 0.5,
        z: -Math.random() * 2 - 1
      }))
    });
  };

  const createPlexusNodes = (group: THREE.Group) => {
    const state = stateRef.current;
    const CONFIG = configRef.current;
    
    for (let i = 0; i < CONFIG.plexusNodes; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 150 + Math.random() * 400;
      
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta) * 0.7;
      const z = CONFIG.vanishingPointZ + Math.random() * (CONFIG.cameraZ - CONFIG.vanishingPointZ + 300);
      
      const nodeGeometry = new THREE.CircleGeometry(CONFIG.nodeSize, 8);
      const nodeMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false
      });
      
      const node = new THREE.Mesh(nodeGeometry, nodeMaterial);
      node.renderOrder = 1;
      node.position.set(x, y, z);
      node.userData = {
        type: 'plexus',
        baseOpacity: 0.6,
        pulsePhase: Math.random() * Math.PI * 2,
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * CONFIG.nodeSpeed * (0.8 + Math.random() * 0.4),
          (Math.random() - 0.5) * CONFIG.nodeSpeed * (0.8 + Math.random() * 0.4),
          (Math.random() - 0.5) * CONFIG.nodeSpeed * 0.6
        )
      };
      
      group.add(node);
      state.plexusNodes.push(node);
      state.allNodes.push(node);
    }
  };

  const addNameNodesToPlexus = (plexusGroup: THREE.Group) => {
    const state = stateRef.current;
    const CONFIG = configRef.current;
    if (!plexusGroup) return;
    
    state.nameObjects.forEach((sprite, index) => {
      const nodeGeometry = new THREE.CircleGeometry(CONFIG.nameNodeSize, 16);
      const nodeMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false
      });
      
      const node = new THREE.Mesh(nodeGeometry, nodeMaterial);
      node.renderOrder = 1;
      // Posicionar o nó ligeiramente atrás do sprite para não cobrir o texto
      node.position.copy(sprite.position);
      node.position.z -= 20;
      node.userData = {
        type: 'name',
        nameIndex: index,
        sprite: sprite,
        baseOpacity: 0.9,
        pulsePhase: Math.random() * Math.PI * 2
      };
      
      const ringGeometry = new THREE.RingGeometry(CONFIG.nameNodeSize + 1, CONFIG.nameNodeSize + 2, 32);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.4,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.renderOrder = 1;
      ring.position.copy(node.position);
      ring.userData = { isNameRing: true };
      node.userData.ring = ring;
      
      plexusGroup.add(node);
      plexusGroup.add(ring);
      state.allNodes.push(node);
    });
  };

  const createPlexusConnections = (group: THREE.Group) => {
    const state = stateRef.current;
    const CONFIG = configRef.current;
    const connectionSet = new Set<string>();
    const connectionCount = new Map<number, number>();
    
    state.allNodes.forEach((_, i) => connectionCount.set(i, 0));
    
    state.allNodes.forEach((nodeA, i) => {
      if (connectionCount.get(i)! >= CONFIG.maxConnectionsPerNode) return;
      
      const neighbors: { index: number; distance: number }[] = [];
      state.allNodes.forEach((nodeB, j) => {
        if (i === j) return;
        const distance = nodeA.position.distanceTo(nodeB.position);
        if (distance < CONFIG.plexusConnectionDist) {
          neighbors.push({ index: j, distance });
        }
      });
      
      neighbors.sort((a, b) => a.distance - b.distance);
      
      for (const neighbor of neighbors) {
        const j = neighbor.index;
        const distance = neighbor.distance;
        
        if (connectionCount.get(i)! >= CONFIG.maxConnectionsPerNode) break;
        if (connectionCount.get(j)! >= CONFIG.maxConnectionsPerNode) continue;
        
        const key = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (connectionSet.has(key)) continue;
        connectionSet.add(key);
        
        connectionCount.set(i, connectionCount.get(i)! + 1);
        connectionCount.set(j, connectionCount.get(j)! + 1);
        
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(6);
        positions[0] = nodeA.position.x;
        positions[1] = nodeA.position.y;
        positions[2] = nodeA.position.z;
        positions[3] = state.allNodes[j].position.x;
        positions[4] = state.allNodes[j].position.y;
        positions[5] = state.allNodes[j].position.z;
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        
        const distFactor = 1 - (distance / CONFIG.plexusConnectionDist);
        
        const material = new THREE.LineBasicMaterial({
          color: 0x00ffff,
          transparent: true,
          opacity: CONFIG.plexusOpacity * distFactor,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false
        });
        
        const line = new THREE.Line(geometry, material);
        line.renderOrder = 0;
        
        const isDecorative = nodeA.userData.type === 'plexus' && state.allNodes[j].userData.type === 'plexus';
        
        line.userData = {
          nodeAIndex: i,
          nodeBIndex: j,
          baseOpacity: CONFIG.plexusOpacity * distFactor,
          pulsePhase: Math.random() * Math.PI * 2,
          isDecorative: isDecorative
        };
        
        group.add(line);
        state.plexusLines.push(line);
      }
    });
  };

  const createNeuralConnections = (scene: THREE.Scene) => {
    const state = stateRef.current;
    const plexusGroup = new THREE.Group();
    plexusGroup.name = 'plexusGroup';
    
    createPlexusNodes(plexusGroup);
    addNameNodesToPlexus(plexusGroup);
    createPlexusConnections(plexusGroup);
    
    scene.add(plexusGroup);
  };

  const createCentralGlow = (scene: THREE.Scene) => {
    const CONFIG = configRef.current;
    const glowGroup = new THREE.Group();
    
    const glowGeometry = new THREE.CircleGeometry(30, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.renderOrder = 0;
    glow.position.z = CONFIG.vanishingPointZ;
    glowGroup.add(glow);
    
    for (let i = 0; i < 4; i++) {
      const ringGeometry = new THREE.RingGeometry(20 + i * 15, 25 + i * 15, 64);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.3 - i * 0.05,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.renderOrder = 0;
      ring.position.z = CONFIG.vanishingPointZ + 10;
      ring.userData.pulsePhase = i * 0.5;
      glowGroup.add(ring);
    }
    
    scene.add(glowGroup);
  };

  const highlightName = (name: string) => {
    const state = stateRef.current;
    const CONFIG = configRef.current;
    if (state.isHighlighting) return;
    
    state.isHighlighting = true;
    state.highlightedName = name;
    
    // Converter posição 3D para posição de tela
    if (cameraRef.current && rendererRef.current) {
      const pos3D = new THREE.Vector3(
        CONFIG.highlightPosition.x,
        CONFIG.highlightPosition.y,
        CONFIG.highlightPosition.z
      );
      pos3D.project(cameraRef.current);
      
      const canvas = rendererRef.current.domElement;
      const left = ((pos3D.x + 1) / 2) * canvas.clientWidth;
      const top = ((-pos3D.y + 1) / 2) * canvas.clientHeight;
      
      setHighlightPosition({
        top: `${top}px`,
        left: `${left}px`
      });
    }
    
    setHighlightedText(name);
    setIsHighlightVisible(true);
    
    const targetSprite = state.nameObjects.find(obj => obj.userData.name === name);
    if (targetSprite) {
      targetSprite.userData.isHighlighted = true;
    }
    
    setTimeout(() => {
      resetHighlight();
    }, CONFIG.highlightDuration);
  };

  // Atualizar ref para a função highlightName (para uso no polling)
  useEffect(() => {
    highlightNameRef.current = highlightName;
  });

  const resetHighlight = () => {
    const state = stateRef.current;
    const CONFIG = configRef.current;
    const highlightedSprite = state.nameObjects.find(s => s.userData.isHighlighted);
    
    state.isHighlighting = false;
    state.highlightedName = null;
    
    setIsHighlightVisible(false);
    
    if (highlightedSprite) {
      state.isReturning = true;
      state.returningSprite = highlightedSprite;
      state.returnProgress = 0;
      
      highlightedSprite.userData.returnStartPos = new THREE.Vector3(
        CONFIG.highlightPosition.x,
        CONFIG.highlightPosition.y,
        CONFIG.highlightPosition.z
      );
      
      highlightedSprite.userData.returnMidPos = new THREE.Vector3(
        0, 0,
        CONFIG.vanishingPointZ + 100
      );
      
      highlightedSprite.material.opacity = 1;
      highlightedSprite.position.copy(highlightedSprite.userData.returnStartPos);
    }
    
    state.nameObjects.forEach(sprite => {
      sprite.userData.isHighlighted = false;
    });
  };

  const handleHighlight = () => {
    if (selectedName) {
      highlightName(selectedName);
    }
  };

  const handleRandomHighlight = () => {
    const state = stateRef.current;
    const randomIndex = Math.floor(Math.random() * state.visibleNames.length);
    highlightName(state.visibleNames[randomIndex]);
  };

  // Ref para controlar o polling do túnel
  const tunelPollingRef = useRef<NodeJS.Timeout | null>(null);
  const highlightNameRef = useRef<((name: string) => void) | null>(null);

  // Função para buscar próximo nome do túnel
  const fetchTunelUser = useCallback(async () => {
    const state = stateRef.current;
    const config = configRef.current;
    
    // Não buscar se já estiver destacando ou retornando
    if (state.isHighlighting || state.isReturning) {
      return;
    }
    
    try {
      const response = await apiRequest(getTunelUserUrl(true), {}, [404]);
      
      // 404 é esperado quando não há usuário pendente - ignorar silenciosamente
      if (response.status === 404) {
        return;
      }
      
      const data = await response.json();
      
      if (data.success && data.data?.name) {
        let nameToHighlight = data.data.name;
        
        // Aplicar shortName se configurado
        if (config.shortName) {
          nameToHighlight = getShortName(nameToHighlight);
        }
        
        console.log('🎯 [PainelLED] Nome recebido do túnel:', nameToHighlight);
        
        // Verificar se o nome está na lista visível
        if (state.visibleNames.includes(nameToHighlight)) {
          if (highlightNameRef.current) {
            highlightNameRef.current(nameToHighlight);
          }
        } else {
          // Se não encontrar na lista, ainda assim destacar com o texto
          console.log('⚠️ [PainelLED] Nome não encontrado na lista visível, destacando mesmo assim');
          if (highlightNameRef.current) {
            highlightNameRef.current(nameToHighlight);
          }
        }
      }
    } catch (error: any) {
      console.error('❌ [PainelLED] Erro ao buscar usuário do túnel:', error);
    }
  }, []);

  // Iniciar polling do túnel quando configLoaded
  useEffect(() => {
    if (!configLoaded) return;
    
    const CONFIG = configRef.current;
    // Polling configurável via CONNECTIONS_HIGHLIGHT_POOLING_TIME (em segundos)
    const POLLING_INTERVAL = CONFIG.highlightPoolingTime * 1000;
    
    console.log(`🔄 [PainelLED] Iniciando polling do túnel a cada ${CONFIG.highlightPoolingTime}s...`);
    
    // Primeira busca imediata
    fetchTunelUser();
    
    // Configurar intervalo
    tunelPollingRef.current = setInterval(fetchTunelUser, POLLING_INTERVAL);
    
    return () => {
      if (tunelPollingRef.current) {
        clearInterval(tunelPollingRef.current);
        tunelPollingRef.current = null;
      }
    };
  }, [configLoaded, fetchTunelUser]);

  // Inicialização do Three.js (espera parâmetros carregarem)
  useEffect(() => {
    if (!canvasRef.current || !configLoaded) return;
    
    // Usar configuração carregada da API
    const CONFIG = configRef.current;
    
    const state = stateRef.current;
    
    // Criar cena
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x000510, 0.0008);
    sceneRef.current = scene;
    
    // Criar câmera
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      1,
      3000
    );
    camera.position.z = CONFIG.cameraZ;
    cameraRef.current = camera;
    
    // Criar renderer
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000510, 1);
    rendererRef.current = renderer;
    
    // Clock
    const clock = new THREE.Clock();
    clockRef.current = clock;
    
    // Inicializar pools de nomes (garantir unicidade)
    const uniqueNames = [...new Set(namesList)];
    const shuffled = uniqueNames.sort(() => Math.random() - 0.5);
    state.visibleNames = shuffled.slice(0, CONFIG.visibleNamesCount);
    state.availableNames = shuffled.slice(CONFIG.visibleNamesCount);
    setVisibleNames([...state.visibleNames]);
    
    // Criar elementos visuais
    createTunnel(scene);
    createParticles(scene);
    
    // Criar sprites de nomes
    state.visibleNames.forEach((name, index) => {
      const sprite = createNameSprite(name, index);
      scene.add(sprite);
      state.nameObjects.push(sprite);
    });
    
    createNeuralConnections(scene);
    createCentralGlow(scene);
    
    state.lastSwapTime = performance.now();
    
    // Loop de animação
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      
      const time = clock.getElapsedTime();
      const currentTime = performance.now();
      
      // Verificar timer de rotação
      if (!state.isSwapping && !state.isHighlighting && !state.isReturning &&
          currentTime - state.lastSwapTime > CONFIG.swapInterval) {
        startNameSwap();
      }
      
      // Animar
      animateNameSwap(time);
      animateNames(time);
      updateNeuralConnections(time);
      animateParticles(time);
      animateTunnel(time);
      
      renderer.render(scene, camera);
    };
    
    // Funções de animação
    const startNameSwap = () => {
      if (state.isSwapping || state.isHighlighting || state.isReturning) return;
      if (state.availableNames.length === 0) return;
      
      state.isSwapping = true;
      state.connectionFadeDirection = -1;
      state.connectionsRefreshed = false;
      
      const swapCount = Math.max(1, Math.floor(state.visibleNames.length * CONFIG.swapPercentage));
      const actualSwapCount = Math.min(swapCount, state.availableNames.length);
      
      const shuffledSprites = [...state.nameObjects].sort(() => Math.random() - 0.5);
      const spritesToExit = shuffledSprites.slice(0, actualSwapCount);
      
      // Filtrar nomes disponíveis que NÃO estão na lista visível (segurança contra duplicatas)
      const safeAvailableNames = state.availableNames.filter(name => !state.visibleNames.includes(name));
      const shuffledAvailable = [...safeAvailableNames].sort(() => Math.random() - 0.5);
      const namesToEnter = shuffledAvailable.slice(0, actualSwapCount);
      
      // Só fazer swap se tivermos nomes suficientes para entrar
      if (namesToEnter.length === 0) {
        state.isSwapping = false;
        return;
      }
      
      // Ajustar quantidade de sprites a sair baseado nos nomes disponíveis
      const finalSpritesToExit = spritesToExit.slice(0, namesToEnter.length);
      
      finalSpritesToExit.forEach((sprite, i) => {
        state.exitingSprites.push({
          sprite: sprite,
          startPos: sprite.position.clone(),
          startOpacity: sprite.material.opacity,
          startScale: sprite.scale.x,
          progress: 0,
          newName: namesToEnter[i]
        });
      });
    };
    
    const animateNameSwap = (time: number) => {
      if (!state.isSwapping) return;
      
      let allExitsComplete = true;
      let allEntriesComplete = true;
      
      state.exitingSprites.forEach(exitData => {
        if (exitData.progress < 1) {
          allExitsComplete = false;
          exitData.progress += 0.006;
          
          const easeT = exitData.progress * exitData.progress;
          const targetPos = new THREE.Vector3(0, 0, CONFIG.vanishingPointZ);
          exitData.sprite.position.lerpVectors(exitData.startPos, targetPos, easeT);
          
          const scale = THREE.MathUtils.lerp(exitData.startScale, 10, easeT);
          exitData.sprite.scale.set(scale, scale / 8, 1);
          exitData.sprite.material.opacity = THREE.MathUtils.lerp(exitData.startOpacity, 0, easeT);
        }
      });
      
      if (allExitsComplete && state.exitingSprites.length > 0 && state.enteringSprites.length === 0) {
        state.exitingSprites.forEach(exitData => {
          const oldName = exitData.sprite.userData.name;
          const newName = exitData.newName;
          
          // Remover nome antigo da lista visível
          const visibleIndex = state.visibleNames.indexOf(oldName);
          if (visibleIndex > -1) state.visibleNames.splice(visibleIndex, 1);
          
          // Adicionar nome antigo aos disponíveis (se ainda não estiver)
          if (!state.availableNames.includes(oldName)) {
            state.availableNames.push(oldName);
          }
          
          // Remover novo nome dos disponíveis
          const availableIndex = state.availableNames.indexOf(newName);
          if (availableIndex > -1) state.availableNames.splice(availableIndex, 1);
          
          // Adicionar novo nome aos visíveis (se ainda não estiver)
          if (!state.visibleNames.includes(newName)) {
            state.visibleNames.push(newName);
          }
          
          scene.remove(exitData.sprite);
          exitData.sprite.material.dispose();
          if (exitData.sprite.material.map) exitData.sprite.material.map.dispose();
          
          const spriteIndex = state.nameObjects.indexOf(exitData.sprite);
          if (spriteIndex > -1) state.nameObjects.splice(spriteIndex, 1);
          
          const entryData = createEnteringSprite(newName, state.nameObjects.length);
          state.enteringSprites.push(entryData);
        });
        
        state.exitingSprites.length = 0;
        setVisibleNames([...state.visibleNames]);
      }
      
      state.enteringSprites.forEach(entryData => {
        if (entryData.progress < 1) {
          allEntriesComplete = false;
          entryData.progress += 0.008;
          
          const easeT = 1 - Math.pow(1 - entryData.progress, 2);
          entryData.sprite.position.lerpVectors(entryData.startPos, entryData.targetPos, easeT);
          
          const scale = THREE.MathUtils.lerp(20, entryData.targetScale, easeT);
          entryData.sprite.scale.set(scale, scale / 8, 1);
          entryData.sprite.material.opacity = THREE.MathUtils.lerp(0, 0.8, easeT);
        }
      });
      
      if (allExitsComplete && allEntriesComplete && state.enteringSprites.length > 0) {
        state.enteringSprites.forEach(entryData => {
          entryData.sprite.position.copy(entryData.targetPos);
          entryData.sprite.userData.originalPosition = entryData.targetPos.clone();
          entryData.sprite.scale.set(entryData.targetScale, entryData.targetScale / 8, 1);
          entryData.sprite.material.opacity = 0.8;
        });
        
        state.enteringSprites.length = 0;
        
        if (state.connectionFadeProgress >= 1) {
          state.isSwapping = false;
          state.lastSwapTime = performance.now();
        }
      }
      
      animateConnectionFade();
    };
    
    const animateConnectionFade = () => {
      if (state.connectionFadeDirection === 0) return;
      
      if (state.connectionFadeDirection === -1) {
        state.connectionFadeProgress -= 0.012;
        
        if (state.connectionFadeProgress <= 0) {
          state.connectionFadeProgress = 0;
          
          if (!state.connectionsRefreshed && state.exitingSprites.length === 0 && state.enteringSprites.length > 0) {
            refreshConnections();
            state.connectionsRefreshed = true;
            state.connectionFadeDirection = 1;
          }
        }
      }
      
      if (state.connectionFadeDirection === 1) {
        state.connectionFadeProgress += 0.008;
        
        if (state.connectionFadeProgress >= 1) {
          state.connectionFadeProgress = 1;
          state.connectionFadeDirection = 0;
          
          if (state.enteringSprites.length === 0) {
            state.isSwapping = false;
            state.lastSwapTime = performance.now();
          }
        }
      }
    };
    
    const refreshConnections = () => {
      const existingGroup = scene.getObjectByName('plexusGroup') as THREE.Group;
      if (!existingGroup) return;
      
      // Remover apenas nodes de nome e suas rings, preservando os plexusNodes
      const nodesToRemove: THREE.Object3D[] = [];
      existingGroup.traverse((child: any) => {
        if (child.userData?.type === 'name' || child.userData?.isNameRing) {
          nodesToRemove.push(child);
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        }
      });
      nodesToRemove.forEach(node => existingGroup.remove(node));
      
      // Remover todas as conexões (serão recriadas)
      state.plexusLines.forEach(line => {
        existingGroup.remove(line);
        if (line.geometry) line.geometry.dispose();
        if ((line.material as THREE.Material).dispose) (line.material as THREE.Material).dispose();
      });
      state.plexusLines.length = 0;
      
      // Manter os plexusNodes no allNodes, remover apenas os nameNodes
      state.allNodes = state.allNodes.filter(node => node.userData?.type === 'plexus');
      
      // Recriar nodes de nome e conexões
      addNameNodesToPlexus(existingGroup);
      createPlexusConnections(existingGroup);
    };
    
    const createEnteringSprite = (name: string, index: number) => {
      const sprite = createNameSprite(name, index);
      sprite.material.opacity = 0;
      sprite.scale.set(20, 2.5, 1);
      
      let startPos: THREE.Vector3;
      if (state.plexusNodes.length > 0) {
        const randomNode = state.plexusNodes[Math.floor(Math.random() * state.plexusNodes.length)];
        startPos = randomNode.position.clone();
      } else {
        startPos = new THREE.Vector3(0, 0, CONFIG.vanishingPointZ + 200);
      }
      
      sprite.position.copy(startPos);
      
      const angle = Math.random() * Math.PI * 2;
      const radius = 50 + Math.random() * 250;
      const z = CONFIG.cameraZ - 200 + Math.random() * 500;
      const clampedZ = Math.max(CONFIG.vanishingPointZ + 300, Math.min(CONFIG.cameraZ + 100, z));
      
      const targetPos = new THREE.Vector3(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius,
        clampedZ
      );
      
      sprite.userData.originalPosition = targetPos.clone();
      sprite.userData.targetPosition = targetPos.clone();
      
      scene.add(sprite);
      state.nameObjects.push(sprite);
      
      return {
        sprite: sprite,
        startPos: startPos,
        targetPos: targetPos,
        targetScale: sprite.userData.baseScale,
        progress: 0
      };
    };
    
    const animateNames = (time: number) => {
      if (state.isReturning && state.returningSprite) {
        state.returnProgress += 0.008;
        
        if (state.returnProgress < 1) {
          const easeT = state.returnProgress * state.returnProgress;
          
          state.returningSprite.position.lerpVectors(
            state.returningSprite.userData.returnStartPos,
            state.returningSprite.userData.returnMidPos,
            easeT
          );
          
          const scale = THREE.MathUtils.lerp(state.returningSprite.userData.baseScale, 20, easeT);
          state.returningSprite.scale.set(scale, scale / 8, 1);
          state.returningSprite.material.opacity = THREE.MathUtils.lerp(1, 0, easeT);
        } else {
          // Restaurar sprite para posição e estado original
          state.returningSprite.position.copy(state.returningSprite.userData.originalPosition);
          state.returningSprite.material.opacity = state.returningSprite.userData.originalOpacity;
          state.returningSprite.scale.set(
            state.returningSprite.userData.baseScale,
            state.returningSprite.userData.baseScale / 8,
            1
          );
          state.isReturning = false;
          state.returningSprite = null;
          state.returnProgress = 0;
        }
      }
      
      state.nameObjects.forEach((sprite) => {
        const data = sprite.userData;
        
        if (state.isReturning && sprite === state.returningSprite) return;
        
        if (state.isSwapping) {
          const isExiting = state.exitingSprites.some(e => e.sprite === sprite);
          const isEntering = state.enteringSprites.some(e => e.sprite === sprite);
          if (isExiting || isEntering) return;
        }
        
        const phase = data.phase + time * data.speed;
        
        const offsetX = Math.sin(phase) * data.amplitude.x +
                       Math.sin(phase * 0.7) * data.amplitude.x * 0.5;
        const offsetY = Math.cos(phase * 0.8) * data.amplitude.y +
                       Math.sin(phase * 0.5) * data.amplitude.y * 0.5;
        const offsetZ = Math.sin(phase * 0.6) * data.amplitude.z +
                       Math.cos(phase * 0.3) * data.amplitude.z * 0.3;
        
        data.originalPosition.x += data.velocity.x * 0.1;
        data.originalPosition.y += data.velocity.y * 0.1;
        data.originalPosition.z += data.velocity.z * 0.1;
        
        if (Math.abs(data.originalPosition.x) > 350) data.velocity.x *= -1;
        if (Math.abs(data.originalPosition.y) > 280) data.velocity.y *= -1;
        if (data.originalPosition.z > CONFIG.cameraZ + 50) data.velocity.z = -Math.abs(data.velocity.z);
        if (data.originalPosition.z < CONFIG.vanishingPointZ + 200) data.velocity.z = Math.abs(data.velocity.z);
        
        sprite.position.x = data.originalPosition.x + offsetX;
        sprite.position.y = data.originalPosition.y + offsetY;
        sprite.position.z = data.originalPosition.z + offsetZ;
        
        const depthFactor = THREE.MathUtils.mapLinear(
          sprite.position.z,
          CONFIG.vanishingPointZ,
          CONFIG.cameraZ,
          0.3,
          1
        );
        
        const scaleFactor = THREE.MathUtils.mapLinear(
          sprite.position.z,
          CONFIG.vanishingPointZ,
          CONFIG.cameraZ,
          data.baseScale * 0.25,
          data.baseScale
        );
        sprite.scale.set(scaleFactor, scaleFactor / 8, 1);
        
        if (state.isHighlighting) {
          if (data.isHighlighted) {
            // Mover sprite para posição de destaque (invisível, mas posicionado para animação de retorno)
            sprite.position.set(
              CONFIG.highlightPosition.x,
              CONFIG.highlightPosition.y,
              CONFIG.highlightPosition.z
            );
            sprite.material.opacity = 0;
          } else {
            sprite.material.opacity = depthFactor * data.originalOpacity * CONFIG.highlightOthersOpacity;
          }
        } else {
          sprite.material.opacity = depthFactor * data.originalOpacity;
        }
      });
    };
    
    const updateNeuralConnections = (time: number) => {
      state.plexusNodes.forEach(node => {
        const vel = node.userData.velocity;
        node.position.x += vel.x;
        node.position.y += vel.y;
        node.position.z += vel.z;
        
        if (Math.abs(node.position.x) > 650) vel.x *= -1;
        if (Math.abs(node.position.y) > 450) vel.y *= -1;
        if (node.position.z < CONFIG.vanishingPointZ || node.position.z > CONFIG.cameraZ + 200) vel.z *= -1;
        
        const pulse = Math.sin(time * CONFIG.pulseSpeed + node.userData.pulsePhase) * 0.2 + 0.8;
        (node.material as THREE.MeshBasicMaterial).opacity = node.userData.baseOpacity * pulse;
      });
      
      state.allNodes.forEach(node => {
        if (node.userData.type === 'name') {
          const sprite = node.userData.sprite;
          if (sprite) {
            // Manter o nó ligeiramente atrás do sprite para não cobrir o texto
            node.position.copy(sprite.position);
            node.position.z -= 20;
            if (node.userData.ring) {
              node.userData.ring.position.copy(sprite.position);
              node.userData.ring.position.z -= 20;
              node.userData.ring.rotation.z = time * 0.3;
            }
          }
          
          const pulse = Math.sin(time * 2 + node.userData.pulsePhase) * 0.15 + 0.85;
          (node.material as THREE.MeshBasicMaterial).opacity = node.userData.baseOpacity * pulse * state.connectionFadeProgress;
          
          const isThisNodeHighlighted = state.isHighlighting && sprite && sprite.userData.isHighlighted;
          const isThisNodeReturning = state.isReturning && sprite === state.returningSprite;
          
          if (isThisNodeHighlighted || isThisNodeReturning) {
            (node.material as THREE.MeshBasicMaterial).opacity = 1;
            node.scale.set(1.5, 1.5, 1.5);
            if (node.userData.ring) {
              (node.userData.ring.material as THREE.MeshBasicMaterial).opacity = 0.8;
              node.userData.ring.scale.set(1.5, 1.5, 1.5);
            }
          } else {
            node.scale.set(1, 1, 1);
            if (node.userData.ring) {
              node.userData.ring.scale.set(1, 1, 1);
              (node.userData.ring.material as THREE.MeshBasicMaterial).opacity = 0.3 * state.connectionFadeProgress;
            }
          }
        }
      });
      
      state.plexusLines.forEach(line => {
        const nodeA = state.allNodes[line.userData.nodeAIndex];
        const nodeB = state.allNodes[line.userData.nodeBIndex];
        
        if (!nodeA || !nodeB) return;
        
        const positions = (line.geometry.attributes.position as THREE.BufferAttribute).array as Float32Array;
        positions[0] = nodeA.position.x;
        positions[1] = nodeA.position.y;
        positions[2] = nodeA.position.z;
        positions[3] = nodeB.position.x;
        positions[4] = nodeB.position.y;
        positions[5] = nodeB.position.z;
        line.geometry.attributes.position.needsUpdate = true;
        
        const distance = nodeA.position.distanceTo(nodeB.position);
        const distFactor = Math.max(0.2, 1 - (distance / CONFIG.plexusConnectionDist));
        
        const pulse = Math.sin(time * CONFIG.pulseSpeed * 0.5 + line.userData.pulsePhase) * 0.1 + 0.9;
        
        const avgZ = (nodeA.position.z + nodeB.position.z) / 2;
        const depthFactor = THREE.MathUtils.mapLinear(
          avgZ,
          CONFIG.vanishingPointZ,
          CONFIG.cameraZ,
          0.3,
          1
        );
        
        let finalOpacity = Math.max(CONFIG.plexusMinOpacity, CONFIG.plexusOpacity * distFactor * pulse * depthFactor);
        
        if (state.isHighlighting || state.isReturning) {
          let targetSprite = null;
          
          if (state.isHighlighting) {
            targetSprite = state.nameObjects.find(s => s.userData.isHighlighted);
          } else if (state.isReturning && state.returningSprite) {
            targetSprite = state.returningSprite;
          }
          
          if (targetSprite) {
            const isConnectedToHighlighted =
              (nodeA.userData.type === 'name' && nodeA.userData.sprite === targetSprite) ||
              (nodeB.userData.type === 'name' && nodeB.userData.sprite === targetSprite);
            
            if (isConnectedToHighlighted) {
              finalOpacity = Math.min(1, finalOpacity * 3);
              (line.material as THREE.LineBasicMaterial).color.setHex(0x00ffff);
            } else if (state.isHighlighting && !line.userData.isDecorative) {
              finalOpacity *= CONFIG.highlightOthersOpacity;
            }
          }
        }
        
        if (!line.userData.isDecorative) {
          finalOpacity *= state.connectionFadeProgress;
        }
        
        (line.material as THREE.LineBasicMaterial).opacity = finalOpacity;
      });
    };
    
    const animateParticles = (time: number) => {
      state.particles.forEach(particleData => {
        const positions = (particleData.geometry.attributes.position as THREE.BufferAttribute).array as Float32Array;
        
        for (let i = 0; i < CONFIG.numParticles; i++) {
          const i3 = i * 3;
          const vel = particleData.velocities[i];
          
          positions[i3] += vel.x;
          positions[i3 + 1] += vel.y;
          positions[i3 + 2] += vel.z;
          
          if (positions[i3 + 2] < CONFIG.vanishingPointZ) {
            positions[i3] = (Math.random() - 0.5) * 1500;
            positions[i3 + 1] = (Math.random() - 0.5) * 1000;
            positions[i3 + 2] = CONFIG.cameraZ + 200;
          }
        }
        
        particleData.geometry.attributes.position.needsUpdate = true;
      });
    };
    
    const animateTunnel = (time: number) => {
      state.tunnelLines.forEach((line, index) => {
        if (line.material) {
          const pulse = Math.sin(time * 2 + index * 0.2) * 0.1 + 0.9;
          (line.material as THREE.LineBasicMaterial).opacity = (line.material as THREE.LineBasicMaterial).opacity * pulse;
        }
      });
    };
    
    // Resize handler
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Expor API globalmente
    (window as any).highlightNameFromAPI = (name: string) => {
      if (state.visibleNames.includes(name)) {
        highlightName(name);
        return { success: true, name: name };
      }
      return { success: false, error: 'Nome não encontrado ou não visível atualmente' };
    };
    
    // Iniciar animação
    animate();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationIdRef.current);
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      
      delete (window as any).highlightNameFromAPI;
    };
  }, [configLoaded, namesList]);

  return (
    <div style={styles.container} ref={containerRef}>
      <canvas ref={canvasRef} style={styles.canvas} />
      <div style={styles.title}>{titlePhrase}</div>
      {/* <div style={styles.controls}>
        <select
          style={styles.select}
          value={selectedName}
          onChange={(e) => setSelectedName(e.target.value)}
        >
          <option value="">Selecione um nome para destacar...</option>
          {[...visibleNames].sort().map((name, index) => (
            <option key={`${name}-${index}`} value={name}>{name}</option>
          ))}
        </select>
        <button style={styles.button} onClick={handleHighlight}>
          Destacar Nome
        </button>
        <button style={styles.button} onClick={handleRandomHighlight}>
          Destacar Aleatório
        </button>
      </div> */}
      <div
        style={{
          ...styles.highlightedName,
          top: highlightPosition.top,
          left: highlightPosition.left,
          ...(isHighlightVisible ? styles.highlightedNameVisible : {})
        }}
      >
        {highlightedText}
      </div>
    </div>
  );
};

export default PainelLED;
